import React, { Component } from "react";
import cloneDeep from "lodash/cloneDeep";
import Modal from "@cloudscape-design/components/modal";
import Input from "@cloudscape-design/components/input";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import Select from "@cloudscape-design/components/select";
import Toggle from "@cloudscape-design/components/toggle";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Alert from "@cloudscape-design/components/alert";
import Container from "@cloudscape-design/components/container";
import Header from "@cloudscape-design/components/header";
import FormField from "@cloudscape-design/components/form-field";
import ColumnLayout from "@cloudscape-design/components/column-layout";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { MAX_PROMO_BONUS_BANNER_BYTES, PROMO_BONUS_TRANSLATION_TYPES, promoBonusEditModalStore, promoBonusesStore } from "../stores/promoBonuses";

const getSelectValue = (value) => value?.value || value;
const toSelectOption = (value) => {
	const normalized = getSelectValue(value) || "local";
	return { value: normalized, label: normalized };
};
const rubToMinor = (value) => Math.round((Number.parseFloat(value) || 0) * 100);

class PromoBonusEditModal extends Component {
	updateParams = (values) => {
		const updatedState = cloneDeep(this.props.promoBonusEditModal);
		Object.assign(updatedState, values);
		this.props.setPromoBonusEditModal(updatedState);
	};

	updateParam = (key, value) => {
		this.updateParams({ [key]: value });
	};

	getBannerPreviewUrl = () => {
		const { bannerUrl, bannerPreviewUrl } = this.props.promoBonusEditModal;
		const normalizedBannerUrl = String(bannerUrl || "");

		if (!normalizedBannerUrl) return "";

		if (normalizedBannerUrl.startsWith("http://") || normalizedBannerUrl.startsWith("https://") || normalizedBannerUrl.startsWith("data:image/")) {
			return normalizedBannerUrl;
		}

		return bannerPreviewUrl || "";
	};

	getValidationError = () => {
		const bonus = this.props.promoBonusEditModal;

		if (!bonus.enabled) return "";

		if (!String(bonus.title || "").trim()) return "Title is required";
		if (!String(bonus.subtitle || "").trim()) return "Subtitle is required";
		if (!String(bonus.buttonText || "").trim()) return "Button text is required";
		if (!String(bonus.linkUrl || "").trim()) return "Link URL is required";

		if (bonus.kind === "contactBonus") {
			const amount = Number.parseFloat(bonus.amount);

			if (!Number.isFinite(amount) || amount < 1 || amount > 500) return "Amount RUB must be between 1 and 500";

			if (bonus.depositRequired) {
				const depositFrom = Number.parseFloat(bonus.depositFrom);
				if (!Number.isFinite(depositFrom) || depositFrom < 0) return "Deposit from RUB must be 0 or greater";
			}
		}

		return "";
	};

	save = () => {
		const validationError = this.getValidationError();

		if (validationError) {
			this.props.Interface.addNoty("Error", validationError);
			return;
		}

		const modal = this.props.promoBonusEditModal;
		const nextBonus = {
			key: modal.key,
			kind: modal.kind,
			enabled: Boolean(modal.enabled),
			sort: parseInt(modal.sort, 10),
			bannerUrl: modal.bannerUrl,
			title: modal.title,
			titleType: getSelectValue(modal.titleType),
			subtitle: modal.subtitle,
			subtitleType: getSelectValue(modal.subtitleType),
			buttonText: modal.buttonText,
			buttonTextType: getSelectValue(modal.buttonTextType),
			linkUrl: modal.linkUrl,
		};

		if (modal.kind === "contactBonus") {
			nextBonus.contactType = modal.contactType;
			nextBonus.amount = rubToMinor(modal.amount);
			nextBonus.depositRequired = Boolean(modal.depositRequired);
			nextBonus.depositFrom = modal.depositRequired ? rubToMinor(modal.depositFrom) : 0;
		}

		const nextData = this.props.promoBonuses.data.map((bonus) => (bonus.key === nextBonus.key ? nextBonus : bonus));

		this.props.Interface.savePromoBonuses(nextData)
			.then(() => {
				this.props.Interface.addNoty("Success", "Promo bonus was successfully updated");
				this.props.setPromoBonusEditModal({ ...this.props.promoBonusEditModal, show: false });
			})
			.catch((error) => {
				const normalizedError = typeof error.text === "undefined" ? { text: error.toString() } : error;
				this.props.setPromoBonuses({ ...this.props.promoBonuses, loaded: true, error: normalizedError });
			});
	};

	handleBannerFile = (event) => {
		const file = event.target.files[0];

		if (!file) return;

		if (file.type !== "image/png") {
			this.props.Interface.addNoty("Error", "Please, select a valid PNG image");
			event.target.value = "";
			return;
		}

		if (file.size > MAX_PROMO_BONUS_BANNER_BYTES) {
			this.props.Interface.addNoty("Error", `Image size must not exceed ${MAX_PROMO_BONUS_BANNER_BYTES / 1024 / 1024} MB`);
			event.target.value = "";
			return;
		}

		const reader = new FileReader();
		reader.onload = ({ target }) => {
			this.updateParams({
				bannerUrl: target.result,
				bannerPreviewUrl: target.result,
			});
		};
		reader.readAsDataURL(file);
	};

	renderTextField(label, valueKey, typeKey) {
		const modal = this.props.promoBonusEditModal;

		return (
			<FormField label={label} stretch>
				<Input autoComplete="off" inputMode="text" type="text" disableBrowserAutocorrect={true} placeholder={label} value={modal[valueKey]} onChange={(event) => this.updateParam(valueKey, event.detail.value)} />
				<Box margin={{ top: "xxs" }}>
					<Select filteringType="auto" placeholder={`Select ${label.toLowerCase()} type`} selectedOption={toSelectOption(modal[typeKey])} onChange={(option) => this.updateParam(typeKey, option.detail.selectedOption)} options={PROMO_BONUS_TRANSLATION_TYPES} />
				</Box>
			</FormField>
		);
	}

	render() {
		const { promoBonusEditModal, setPromoBonusEditModal, promoBonuses } = this.props;
		const isContactBonus = promoBonusEditModal.kind === "contactBonus";
		const errorText = promoBonuses.error?.text || "";
		const bannerPreviewUrl = this.getBannerPreviewUrl();

		return (
			<Modal
				size="large"
				visible={promoBonusEditModal.show}
				onDismiss={() => setPromoBonusEditModal({ ...promoBonusEditModal, show: false })}
				header="Edit promo bonus"
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="m">
							<Button variant="normal" onClick={() => setPromoBonusEditModal({ ...promoBonusEditModal, show: false })}>
								Close
							</Button>
							<Button variant="primary" onClick={this.save} loading={!promoBonuses.loaded}>
								Save
							</Button>
						</SpaceBetween>
					</Box>
				}>
				<SpaceBetween direction="vertical" size="m">
					{errorText ? <Alert type="error">{errorText}</Alert> : null}

					<Container>
						<ColumnLayout columns={4} variant="text-grid">
							<Box>
								<Box variant="awsui-key-label">Key</Box>
								<Box>{promoBonusEditModal.key}</Box>
							</Box>
							<Box>
								<Box variant="awsui-key-label">Kind</Box>
								<Box>{promoBonusEditModal.kind}</Box>
							</Box>
							<Box>
								<Box variant="awsui-key-label">Contact</Box>
								<Box>{promoBonusEditModal.contactType || "-"}</Box>
							</Box>
							<Box>
								<Box variant="awsui-key-label">Status</Box>
								<Toggle checked={Boolean(promoBonusEditModal.enabled)} onChange={({ detail }) => this.updateParam("enabled", detail.checked)}>
									{promoBonusEditModal.enabled ? "Enabled" : "Disabled"}
								</Toggle>
							</Box>
						</ColumnLayout>
					</Container>

					<Container header={<Header variant="h3">Content</Header>}>
						<SpaceBetween direction="vertical" size="m">
							<ColumnLayout columns={2}>
								{this.renderTextField("Title", "title", "titleType")}
								{this.renderTextField("Subtitle", "subtitle", "subtitleType")}
							</ColumnLayout>
							{this.renderTextField("Button Text", "buttonText", "buttonTextType")}
						</SpaceBetween>
					</Container>

					<Container header={<Header variant="h3">Display and action</Header>}>
						<SpaceBetween direction="vertical" size="m">
							<ColumnLayout columns={2}>
								<FormField label="Sort">
									<Input autoComplete="off" inputMode="number" type="number" placeholder="Sort" value={String(promoBonusEditModal.sort)} onChange={(event) => this.updateParam("sort", event.detail.value)} />
								</FormField>
								<FormField label="Link URL" stretch>
									<Input autoComplete="off" inputMode="url" type="text" placeholder="Link URL" value={promoBonusEditModal.linkUrl} onChange={(event) => this.updateParam("linkUrl", event.detail.value)} />
								</FormField>
							</ColumnLayout>

							<FormField label="Banner image" description="Upload PNG image up to 1 MB or paste a URL manually." stretch>
								<SpaceBetween direction="vertical" size="s">
									{bannerPreviewUrl ? (
										<img
											alt={`${promoBonusEditModal.key} banner preview`}
											src={bannerPreviewUrl}
											style={{
												width: "220px",
												maxWidth: "100%",
												height: "108px",
												objectFit: "cover",
												borderRadius: "4px",
												background: "#f2f3f3",
											}}
										/>
									) : null}
									<input type="file" accept="image/png" onChange={this.handleBannerFile} />
								</SpaceBetween>
							</FormField>
						</SpaceBetween>
					</Container>

					{isContactBonus ? (
						<Container header={<Header variant="h3">Contact bonus settings</Header>}>
							<SpaceBetween direction="vertical" size="m">
								<Toggle checked={Boolean(promoBonusEditModal.depositRequired)} onChange={({ detail }) => this.updateParam("depositRequired", detail.checked)}>
									{promoBonusEditModal.depositRequired ? "Deposit required" : "Deposit not required"}
								</Toggle>
								<ColumnLayout columns={2}>
									<FormField label="Amount RUB">
										<Input autoComplete="off" inputMode="decimal" type="number" placeholder="Amount RUB" value={String(promoBonusEditModal.amount)} onChange={(event) => this.updateParam("amount", event.detail.value)} />
									</FormField>
									{promoBonusEditModal.depositRequired ? (
										<FormField label="Deposit From RUB">
											<Input autoComplete="off" inputMode="decimal" type="number" placeholder="Deposit From RUB" value={String(promoBonusEditModal.depositFrom)} onChange={(event) => this.updateParam("depositFrom", event.detail.value)} />
										</FormField>
									) : null}
								</ColumnLayout>
							</SpaceBetween>
						</Container>
					) : null}
				</SpaceBetween>
			</Modal>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const promoBonuses = useRecoilValue(promoBonusesStore);
		const setPromoBonuses = useSetRecoilState(promoBonusesStore);
		const promoBonusEditModal = useRecoilValue(promoBonusEditModalStore);
		const setPromoBonusEditModal = useSetRecoilState(promoBonusEditModalStore);

		return <Component {...props} promoBonuses={promoBonuses} setPromoBonuses={setPromoBonuses} promoBonusEditModal={promoBonusEditModal} setPromoBonusEditModal={setPromoBonusEditModal} />;
	};
}

export default withRecoil(PromoBonusEditModal);
